import "./InvoiceTaxes.scss";
import {
	DEFAULT_RET_ISR_PCT,
	DEFAULT_RET_IVA_PCT,
	AmountMode,
	InvoiceTaxConfig,
	computeBreakdownFor,
	isPersonaMoral,
	money,
	validateTaxConfig,
} from "@services/invoiceTaxes";

// Porcentajes editables de impuestos y desglose de la factura (solo personal).
// `amount` es lo cobrado (mode "total") o el subtotal antes de impuestos (mode "subtotal").
export function InvoiceTaxesFields({
	amount,
	mode = "total",
	rfc,
	value,
	onChange,
}: {
	amount: number;
	mode?: AmountMode;
	rfc: string;
	value: InvoiceTaxConfig;
	onChange: (cfg: InvoiceTaxConfig) => void;
}) {
	const setPct = (field: keyof InvoiceTaxConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
		onChange({ ...value, [field]: e.target.value === "" ? NaN : parseFloat(e.target.value) });
	const error = validateTaxConfig(value);
	const breakdown = !error ? computeBreakdownFor(mode, amount, value) : null;
	const centsOff = breakdown && mode === "total" ? Math.round((breakdown.total - amount) * 100) : 0;
	const rfcType = (rfc ?? "").trim().length === 12 ? "persona moral" : (rfc ?? "").trim().length === 13 ? "persona física" : "";

	return (
		<div className="invoice-taxes">
			<div className="it-title">
				Impuestos {rfcType && <span className="it-rfc-type">(RFC de {rfcType})</span>}
			</div>
			<div className="it-type" role="radiogroup" aria-label="Tipo de cliente">
				<button
					type="button"
					className={!value.withholdings ? "on" : ""}
					onClick={() => onChange({ ...value, withholdings: false })}
				>
					Persona física
				</button>
				<button
					type="button"
					className={value.withholdings ? "on" : ""}
					onClick={() =>
						onChange({
							...value,
							withholdings: true,
							retIvaPct: isNaN(value.retIvaPct) ? DEFAULT_RET_IVA_PCT : value.retIvaPct,
							retIsrPct: isNaN(value.retIsrPct) ? DEFAULT_RET_ISR_PCT : value.retIsrPct,
						})
					}
				>
					Persona moral (con retenciones)
				</button>
			</div>
			<div className="it-rates">
				<label>
					IVA %
					<input type="number" step="0.0001" min={0} max={16} value={isNaN(value.ivaPct) ? "" : value.ivaPct} onChange={setPct("ivaPct")} />
				</label>
				{value.withholdings && (
					<>
						<label>
							Ret. IVA %
							<input type="number" step="0.0001" min={0} max={16} value={isNaN(value.retIvaPct) ? "" : value.retIvaPct} onChange={setPct("retIvaPct")} />
						</label>
						<label>
							Ret. ISR %
							<input type="number" step="0.0001" min={0} max={35} value={isNaN(value.retIsrPct) ? "" : value.retIsrPct} onChange={setPct("retIsrPct")} />
						</label>
					</>
				)}
			</div>
			{value.withholdings && !isPersonaMoral(rfc) && (
				<div className="it-warning">Ojo: el RFC no es de empresa (12 caracteres); revisa que de verdad lleve retenciones.</div>
			)}
			{error && <div className="it-error">{error}</div>}
			{breakdown && (
				<table className="it-breakdown">
					<tbody>
						<tr>
							<td>Subtotal</td>
							<td>{money(breakdown.subtotal)}</td>
						</tr>
						<tr>
							<td>IVA {value.ivaPct} %</td>
							<td>{money(breakdown.iva)}</td>
						</tr>
						{value.withholdings && (
							<>
								<tr>
									<td>Retención IVA {value.retIvaPct} %</td>
									<td>−{money(breakdown.retIva)}</td>
								</tr>
								<tr>
									<td>Retención ISR {value.retIsrPct} %</td>
									<td>−{money(breakdown.retIsr)}</td>
								</tr>
							</>
						)}
						<tr className="it-total">
							<td>Total</td>
							<td>{money(breakdown.total)}</td>
						</tr>
					</tbody>
				</table>
			)}
			{breakdown && centsOff !== 0 && (
				<div className="it-warning">
					Por redondeo, el total de la factura queda {Math.abs(centsOff)} centavo{Math.abs(centsOff) === 1 ? "" : "s"}{" "}
					{centsOff > 0 ? "arriba" : "abajo"} de lo cobrado ({money(amount)}).
				</div>
			)}
		</div>
	);
}
